import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import { hashPassword } from '../../utils/crypto';
import toast from 'react-hot-toast';
import { sendLoginAlertEmail } from '../../utils/emailService';

// Force Login Modal Component
const ForceLoginModal = ({ isOpen, onClose, onForceLogin, identifier, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-slate-800 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-700">
        {/* Warning Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-3">
          Session Already Active
        </h3>

        {/* Message */}
        <p className="text-gray-300 text-center mb-2">
          You are already logged in on another device.
        </p>
        <p className="text-gray-400 text-sm text-center mb-6">
          Account: <span className="text-blue-400 font-medium">{identifier}</span>
        </p>

        {/* Info Box */}
        <div className="bg-slate-700/50 rounded-xl p-4 mb-6">
          <p className="text-gray-300 text-sm text-center">
            If you continue, you will be logged out from the other device and a new session will be created here.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-gray-200 font-semibold rounded-xl transition duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onForceLogin}
            disabled={loading}
            className="flex-1 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const EmailLoginTab = ({ onLoginSuccess, onForgotPassword }) => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForceLoginModal, setShowForceLoginModal] = useState(false);
  const [forceLoginLoading, setForceLoginLoading] = useState(false);
  const [hashedPasswordCache, setHashedPasswordCache] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // Handle Force Login - logout from other device and login here
  const handleForceLogin = async () => {
    setForceLoginLoading(true);
    
    try {
      console.log('=== FORCE LOGIN STARTED ===');
      console.log('Calling force-login API for:', formData.identifier);
      
      const response = await fetch(`${API_BASE_URL}/users/force-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: formData.identifier,
          password: hashedPasswordCache
        })
      });

      const data = await response.json();
      console.log('Force login response:', data);

      if (response.ok && response.status === 200) {
        // Extract user data and token from response
        const userData = data.data?.user || data.user || data.data;
        const token = data.data?.token || data.token || data.accessToken || data.auth_token;
        
        if (userData && token) {
          setShowForceLoginModal(false);
          toast.success('Logged in successfully!');
          // Send login alert email before redirect
          await sendLoginAlertEmail({
            name: userData.full_name || userData.name || userData.username || 'User',
            email: userData.email
          }).catch(console.error);
          onLoginSuccess(userData, token);
        } else {
          console.error('Invalid response structure:', data);
          setError('Invalid response from server.');
          setShowForceLoginModal(false);
        }
      } else {
        setError(data.message || 'Force login failed. Please try again.');
        setShowForceLoginModal(false);
      }
    } catch (err) {
      console.error('Force login error:', err);
      setError('An error occurred. Please try again.');
      setShowForceLoginModal(false);
    } finally {
      setForceLoginLoading(false);
    }
  };

  // Close force login modal
  const handleCloseForceLoginModal = () => {
    setShowForceLoginModal(false);
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const hashedPwd = await hashPassword(formData.password);
      // Cache hashed password for force login
      setHashedPasswordCache(hashedPwd);
      
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: formData.identifier,
          password: hashedPwd
        })
      });

      const data = await response.json();

      // Check if session already exists on another device
      if (data.sessionExists === true) {
        console.log('Session already exists, showing force login modal');
        setShowForceLoginModal(true);
        setLoading(false);
        return;
      }

      if (response.ok && response.status === 200) {
        // Extract user data and token from response
        const userData = data.data?.user || data.user || data.data;
        const token = data.data?.token || data.token || data.accessToken || data.auth_token;
        
        if (userData && token) {
          // Send login alert email before redirect
          await sendLoginAlertEmail({
            name: userData.full_name || userData.name || userData.username || 'User',
            email: userData.email
          }).catch(console.error);
          onLoginSuccess(userData, token);
        } else {
          console.error('Invalid response structure:', data);
          setError('Invalid response from server.');
        }
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Force Login Modal */}
      <ForceLoginModal
        isOpen={showForceLoginModal}
        onClose={handleCloseForceLoginModal}
        onForceLogin={handleForceLogin}
        identifier={formData.identifier}
        loading={forceLoginLoading}
      />

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 relative">
      {/* Loading Bar */}
      {loading && (
        <div className="absolute -top-4 left-0 right-0 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 animate-[loadingBar_2s_linear]" style={{animation: 'loadingBar 2s linear', animationFillMode: 'forwards'}} />
        </div>
      )}
      <style>{`
        @keyframes loadingBar {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      <div>
        <label className="block text-xs sm:text-sm font-medium text-slate-600 mb-1.5 sm:mb-2">
          Email Address
        </label>
        <div className="relative">
          <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            name="identifier"
            value={formData.identifier}
            onChange={handleChange}
            placeholder="Enter your email address"
            className="w-full pl-11 pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-200 rounded-2xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:bg-white"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-slate-600 mb-1.5 sm:mb-2">
          Password
        </label>
        <div className="relative">
          <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
            className="w-full pl-11 pr-11 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-200 rounded-2xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:bg-white"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      <div className="text-right -mt-2">
        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          Forgot your password?
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold text-base py-3 px-4 rounded-2xl transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
      >
        {loading ? 'Signing in...' : (
          <>
            Sign In
            <ArrowRight size={18} />
          </>
        )}
      </button>
    </form>
    </>
  );
};

export default EmailLoginTab;
